import classNames from "classnames";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import DOMPurify from 'isomorphic-dompurify';
import SocialShare from "../../Brand/SocialShare";
import { getPrimaryRating } from '../../../services/bazaarvoice-api';
import i18nMessages from '../../../constants/messages/pt-BR';
import Typography from "../../Generic/Typography";
import Image from "next/image";
import Rating from '../../Generic/Rating';
import cardStyles from '../Product/card-tw-styles';
import dynamic from 'next/dynamic';

// Dynamically import BuyNowLiteComponent with SSR disabled to prevent hook execution during server render
const BuyNowLiteComponent = dynamic(() => import('../../Generic/BuyNowLite'), { 
  ssr: false, 
  loading: () => null 
});

const HeaderNav = ({ showHeader, pageData, headerImage, productName, locale }) => {
    const [ratingData, setRatingData] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const desktopHeaderRef = React.useRef(null);
    const mobileHeaderRef = React.useRef(null);
    const hasFocusedStickyRef = React.useRef(false);
    const previousFocusRef = React.useRef(null);
    
    const handleWindowChange = () => {
        const isMobileDevice = window.innerWidth < 767;
        setIsMobile(isMobileDevice);
    };
    
    useEffect(() => {
        handleWindowChange();
        window.addEventListener(i18nMessages.resize, handleWindowChange);
        
        const productIdList = [];
        let productDetail = pageData;
        if (productDetail && productDetail.ratingAndReviewId) {
            productIdList.push(productDetail && productDetail.ratingAndReviewId);
            getPrimaryRating(productIdList, locale)
                .then(res => {
                    if (res.data.Results.length > 0) {
                        setRatingData(res.data.Results[0]);
                    }
                })
                .catch(err => {
                    console.error('Failed to fetch primary rating:', err);
                });
        }
        
        return () => {
            window.removeEventListener(i18nMessages.resize, handleWindowChange);
        };
    }, [])

    // Focus management for sticky header - robust for all zoom levels
    useEffect(() => {
        if (!showHeader) {
            hasFocusedStickyRef.current = false;
            previousFocusRef.current = null;
            return;
        }

        const isVisibleFocusable = (el) => {
            if (!el) return false;
            if (el.hasAttribute('disabled')) return false;
            if (el.getAttribute('aria-hidden') === 'true') return false;

            const style = window.getComputedStyle(el);
            if (style.display === 'none' || style.visibility === 'hidden') return false;

            const rect = el.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        };

        const getFocusable = (root) =>
            Array.from(root.querySelectorAll(
                'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )).filter(isVisibleFocusable);

        const getActiveHeaderEl = () => {
            const desktopEl = desktopHeaderRef.current;
            const mobileEl = mobileHeaderRef.current;

            if (desktopEl && desktopEl.offsetParent !== null) return desktopEl;
            if (mobileEl && mobileEl.offsetParent !== null) return mobileEl;
            return desktopEl || mobileEl;
        };

        const getNextFocusableAfter = (fromEl, excludeRoot) => {
            if (!fromEl) return null;

            const walker = document.createTreeWalker(
                document.body,
                NodeFilter.SHOW_ELEMENT,
                {
                    acceptNode(node) {
                        const el = node;
                        if (excludeRoot && excludeRoot.contains(el)) return NodeFilter.FILTER_REJECT;
                        if (!isVisibleFocusable(el)) return NodeFilter.FILTER_SKIP;

                        const focusable = el.matches?.(
                            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                        );
                        return focusable ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
                    }
                }
            );

            walker.currentNode = fromEl;
            return walker.nextNode();
        };

        const resetOnResize = () => {
            hasFocusedStickyRef.current = false;
            previousFocusRef.current = null;
        };

        const handleKeyDown = (e) => {
            if (e.key !== 'Tab' || e.shiftKey) return;

            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;

            const headerEl = getActiveHeaderEl();
            if (!headerEl) return;

            const activeEl = document.activeElement;

            // If focus is currently inside sticky header
            if (headerEl.contains(activeEl)) {
                const focusables = getFocusable(headerEl);
                if (!focusables.length) return;

                const last = focusables[focusables.length - 1];

                // If on the last element, exit to the next focusable AFTER the element we came from
                if (activeEl === last && previousFocusRef.current) {
                    e.preventDefault();
                    const next = getNextFocusableAfter(previousFocusRef.current, headerEl);

                    if (next) next.focus();
                    else {
                        // fallback: find first focusable outside sticky
                        const outside = getFocusable(document).filter(el => !headerEl.contains(el));
                        if (outside[0]) outside[0].focus();
                    }
                }
                return;
            }

            // If not inside sticky and we haven't redirected yet
            if (!hasFocusedStickyRef.current) {
                const focusables = getFocusable(headerEl);
                if (!focusables.length) return;

                e.preventDefault();
                hasFocusedStickyRef.current = true;
                previousFocusRef.current = activeEl;
                focusables[0].focus();
            }
        };

        window.addEventListener('resize', resetOnResize);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('resize', resetOnResize);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showHeader]);

    return (
        <div
            className={classNames(
                "dynamic-header",
                `${showHeader ? "setTop" : "removedTop"}`
            )}
        >
            <div
                ref={desktopHeaderRef}
                className={classNames(
                    "min-h-80 z-12 fixed left-0 top-70 lg:bg-lightGrey shadow-md py-25 w-full",
                    showHeader ? "hidden lg:block" : "hidden"
                )}
            >
                <div className="container mx-auto">
                    <div className="flex flex-wrap justify-between items-center">
                        <div className="production-section flex items-center gap-15 justify-center">
                            <div 
                                className="w-100 h-150 rounded-full overflow-hidden flex justify-center items-center headerNavHeight"
                                tabIndex={0}
                                role="img"
                                aria-label={`Sticky product header: ${DOMPurify.sanitize(productName || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })} product image`}
                            >
                                <Image
                                    width={50}
                                    height={50}
                                    src={headerImage}
                                    alt="product"
                                    className="w-full h-full object-cover headerImage"
                                />
                            </div>
                            <Typography dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(productName),
                            }}
                                component="h2" 
                                className="font-HelveticaNeueBold uppercase text-black text-22 block max-w-350 font-bold ml-10 navHeading"
                            />
                            <div className="rating-review ml-40">
                                
                                <div
                                    data-bv-show="inline_rating"
                                    data-bv-product-id={
                                        pageData?.ratingAndReviewId
                                    }
                                    data-bv-redirect-url=""
                                    tabIndex={0}
                                    aria-label={
                                        ratingData 
                                            ? `${ratingData.AverageRating || 0} de 5 estrelas. ${ratingData.TotalReviewCount || 0} ${ratingData.TotalReviewCount === 1 ? 'avaliação' : 'avaliações'}`
                                            : "Product ratings and reviews"
                                    }
                                ></div>
                            </div>
                            <div className={"social-share-section"}>
                                <SocialShare
                                    shareIcon={i18nMessages.shareProduct}
                                    productName={pageData?.productName || productName}
                                    shareImage={
                                        pageData.pageMetadata &&
                                        pageData.pageMetadata.ogImage &&
                                        pageData.pageMetadata.ogImage.url
                                    }
                                    shareDescription={
                                        pageData.pageMetadata &&
                                        pageData.pageMetadata
                                            .openGraphDescription
                                    }
                                    template={i18nMessages.productSticky}
                                />
                                <Typography
                                    component="span"
                                    className={
                                        "font-HelveticaNeueLight text-base text-grey"
                                    }
                                    title={i18nMessages.share}
                                >
                                    {i18nMessages.toShare}
                                </Typography>
                            </div>
                        </div>
                        <div className="button-container">
                            <div id="buy-now-mob" className={!pageData?.isNewTemplate ? "try-now-btn text-center" : undefined}>
                                                                    <BuyNowLiteComponent
                                                                        gtinId={pageData && pageData.gtin}
                                                                        variant={
                                                                            pageData &&
                                                                            pageData.variantsCollection &&
                                                                            pageData.variantsCollection.items
                                                                        }
                                                                        linkText={i18nMessages.buyNow}
                                                                        productShortName={pageData?.productShortName}
                                                                        className={pageData?.isNewTemplate ? "px-60 float-right  py-10 hover:border-1 uppercase hover:border-[#a4783fff]  cursor-pointer button-tertiary text-white border rounded-30" : "btn btn-secondary event_buy_now event_buy_now_choose_retailer"}
                                                                    />
                                                                </div>
                            {/* <button className="px-60 float-right  py-10 hover:border-1 uppercase hover:border-[#a4783fff]  cursor-pointer button-tertiary text-white border rounded-30">
                                {i18nMessages.buyNow}
                            </button> */}
                        </div>
                    </div>
                </div>
            </div>
            <div 
                ref={mobileHeaderRef}
                className={showHeader ? "lg:hidden" : "hidden"}
            >
                <div className="w-full p-20 flex items-end pb-10">
                    <div className="w-60p">
                        <Typography dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(productName),
                        }} 
                            component="h2" 
                            className="font-HelveticaNeueBold uppercase text-black text-16 font-bold ml-10"
                            tabIndex={0}
                            role="heading"
                            aria-label={`Sticky product header: ${productName?.replace(/<[^>]*>/g, '')}`}
                        />
                        <div className={!pageData?.isNewTemplate ? "try-now-btn ml-10 my-10" : undefined}>
                            <BuyNowLiteComponent
                                gtinId={pageData && pageData.gtin}
                                variant={
                                    pageData &&
                                    pageData.variantsCollection &&
                                    pageData.variantsCollection.items
                                }
                                linkText={i18nMessages.buyNow}
                                productShortName={pageData?.productShortName}
                                className={pageData?.isNewTemplate ? "px-30 ml-5 my-10 py-10 hover:border-1 uppercase hover:border-[#a4783fff]  cursor-pointer button-tertiary text-white border rounded-30" : "btn btn-secondary event_buy_now event_buy_now_choose_retailer"}
                            />
                        </div>
                        <div className="compare-view pl-10 mt-5 flex gap-10 items-center">
                            <div
                                data-bv-show="inline_rating"
                                data-bv-product-id={pageData?.ratingAndReviewId}
                                data-bv-redirect-url=""
                                tabIndex={0}
                                aria-label="Product ratings and reviews"
                            ></div>
                            <div className="ml-20">
                                <SocialShare
                                    shareIcon={i18nMessages.shareProduct}
                                    productName={pageData?.productName || productName}
                                    shareImage={
                                        pageData.pageMetadata &&
                                        pageData.pageMetadata.ogImage &&
                                        pageData.pageMetadata.ogImage.url
                                    }
                                    shareDescription={
                                        pageData.pageMetadata &&
                                        pageData.pageMetadata
                                            .openGraphDescription
                                    }
                                    template={i18nMessages.productSticky}
                                />
                                <Typography
                                    component="span"
                                    className={
                                        "font-HelveticaNeueLight text-base text-grey"
                                    }
                                    title={i18nMessages.share}
                                >
                                    {i18nMessages.toShare}
                                </Typography>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

HeaderNav.propTypes = {
    showHeader: PropTypes.bool.isRequired,
    productName: PropTypes.string.isRequired,
    pageData: PropTypes.object.isRequired,
    headerImage: PropTypes.string,
    locale: PropTypes.string,
};

export default HeaderNav;